import React from "react";
import PageShell, { PageHeader } from "@/components/PageShell";
import { COMPANY, MANIFESTO, CREDENTIALS } from "@/data/site";

const MILESTONES = [
  { label: "A szakma", title: "Generációk tapasztalata", body: "A tetőfedés és ácsmunka fortélyait a gyakorlatban, valódi tetőkön sajátítottuk el." },
  { label: "A csapat", title: "Saját, összeszokott brigád", body: "Állandó csapattal dolgozunk, így minden munkán ugyanazt a minőséget és odafigyelést kapja." },
  { label: "A munka", title: "Sokféle tető, egy igényesség", body: "A hagyományos cseréptetőtől a modern lemezfedésig és a műemléki felújításig számos feladaton dolgoztunk." },
  { label: "Az érték", title: "Megbízható, tartós megoldások", body: "Minden tetőt úgy készítünk, hogy hosszú évekig gond nélkül szolgáljon — kompromisszumok nélkül." },
];

const QUOTES = [
  { q: "A tetőt nem azért csináljuk, hogy gyorsan kész legyen. Azért csináljuk, hogy hosszú évek múlva is álljon.", attr: "Totál-Bádogos Kft." },
];

export default function RolamPage() {
  return (
    <PageShell>
      <PageHeader
        number="05"
        eyebrow="Rólunk"
        title="Totál-Bádogos Kft.,"
        titleAccent="a tetőfedés mesterei."
        lead={`${COMPANY.leadTitle} · ács mesteri képesítéssel · ${COMPANY.serviceArea}.`}
      />

      {/* Photo + manifesto */}
      <section className="section" style={{ background: "var(--ink)" }}>
        <div className="site-container">
          <div className="ab-grid" style={{ display: "grid", gridTemplateColumns: "1fr", gap: 64 }}>
            <style>{`@media (min-width: 1024px) { .ab-grid { grid-template-columns: 0.42fr 0.58fr !important; gap: 80px !important; align-items: start !important; } }`}</style>

            <div style={{ position: "relative", maxWidth: 420 }}>
              <div className="photo" style={{ aspectRatio: "4 / 5", width: "100%", backgroundImage: 'url("/projects/proj-2.jpg")', backgroundSize: "cover", backgroundPosition: "center" }} />
              <div className="body" style={{ marginTop: 28 }}>
                <p style={{ margin: 0 }}>
                  Családi vállalkozás, összeszokott csapattal. Minden munkára ugyanazt az igényességet és írásos garanciát adjuk.
                </p>
              </div>
            </div>

            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
                <span style={{ height: 1, background: "var(--gold)", width: 32 }} />
                <span className="eyebrow">Elveink</span>
              </div>
              <p className="lead" style={{ margin: 0, fontSize: 16, marginBottom: 40 }}>
                Néhány elv, amitől a tetőink hosszú évekig megbízhatóan szolgálnak.
              </p>

              <div style={{ borderTop: "1px solid var(--line)" }}>
                {MANIFESTO.map((m, i) => (
                  <div key={m.n} data-testid={`manifesto-${i}`} style={{
                    display: "grid", gridTemplateColumns: "48px 1fr", gap: 28,
                    padding: "26px 0", borderBottom: "1px solid var(--line)",
                  }}>
                    <span className="font-display" style={{ fontSize: 14, color: "var(--gold)", letterSpacing: "0.04em" }}>{m.n}</span>
                    <div>
                      <h3 className="h3" style={{ margin: 0, marginBottom: 8 }}>{m.title}</h3>
                      <p className="body" style={{ margin: 0, maxWidth: 560 }}>{m.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pull quote */}
      <section style={{ background: "var(--ink-2)", borderTop: "1px solid var(--line)", borderBottom: "1px solid var(--line)", padding: "96px 0", position: "relative" }} className="bg-grid">
        <div className="site-container" style={{ position: "relative", zIndex: 1 }}>
          <span aria-hidden style={{ fontFamily: "'DM Serif Display', serif", fontSize: 140, color: "var(--gold)", lineHeight: 0.5, opacity: 0.4, display: "block" }}>&ldquo;</span>
          <blockquote style={{ margin: "0 0 28px", fontFamily: "Manrope, sans-serif", fontWeight: 500, fontSize: "clamp(1.6rem, 3.2vw, 2.4rem)", lineHeight: 1.25, letterSpacing: "-0.025em", color: "var(--text)", maxWidth: 880 }}>
            {QUOTES[0].q}
          </blockquote>
          <span className="eyebrow">— {QUOTES[0].attr}</span>
        </div>
      </section>

      {/* Timeline — vertical milestones */}
      <section className="section" style={{ background: "var(--ink)" }}>
        <div className="site-container">
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
            <span style={{ height: 1, background: "var(--gold)", width: 32 }} />
            <span className="eyebrow">Mérföldkövek</span>
          </div>
          <h2 className="h2" style={{ margin: 0, marginBottom: 56, maxWidth: 720 }}>
            Honnan indultunk, <span style={{ color: "var(--gold)" }}>hova tartunk.</span>
          </h2>

          <div style={{ position: "relative", paddingLeft: 32 }}>
            {/* vertical line */}
            <div style={{ position: "absolute", left: 7, top: 4, bottom: 4, width: 1, background: "var(--line)" }} aria-hidden />

            {MILESTONES.map((m, i) => (
              <div
                key={m.title}
                data-testid={`milestone-${i}`}
                style={{ position: "relative", paddingBottom: i < MILESTONES.length - 1 ? 40 : 0 }}
              >
                <span aria-hidden style={{
                  position: "absolute", left: -32, top: 6,
                  width: 14, height: 14, background: "var(--ink)",
                  border: "1px solid var(--gold)",
                }} />
                <span className="eyebrow" style={{ color: "var(--gold)" }}>{m.label}</span>
                <h3 className="h3" style={{ margin: "8px 0 6px" }}>{m.title}</h3>
                <p className="body" style={{ margin: 0, maxWidth: 560 }}>{m.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Credentials grid */}
      <section className="section bg-grid" style={{ background: "var(--ink-2)", borderTop: "1px solid var(--line)", borderBottom: "1px solid var(--line)", position: "relative" }}>
        <div className="site-container" style={{ position: "relative", zIndex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
            <span style={{ height: 1, background: "var(--gold)", width: 32 }} />
            <span className="eyebrow">Tanúsítványok · Biztosítások</span>
          </div>
          <h2 className="h2" style={{ margin: 0, marginBottom: 48, maxWidth: 720 }}>
            <span style={{ color: "var(--gold)" }}>Hivatalosan</span> igazolható.
          </h2>

          <style>{`
            .creds-grid { grid-template-columns: 1fr; }
            @media (min-width: 600px) { .creds-grid { grid-template-columns: repeat(2, 1fr); } }
            @media (min-width: 900px) { .creds-grid { grid-template-columns: repeat(${CREDENTIALS.length}, 1fr); } }
          `}</style>
          <div className="creds-grid" style={{
            display: "grid",
            gap: 1, background: "var(--line)", border: "1px solid var(--line)",
          }}>
            {CREDENTIALS.map((c, i) => (
              <div key={i} data-testid={`credential-${i}`} style={{ background: "var(--ink-2)", padding: "28px 28px" }}>
                <span className="font-display" style={{ fontSize: 13, color: "var(--gold)", letterSpacing: "0.04em" }}>
                  {String(i + 1).padStart(2, "0")}
                </span>
                <p style={{ margin: "12px 0 0", fontSize: 14, color: "var(--text)", lineHeight: 1.55 }}>
                  {c}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

    </PageShell>
  );
}
